import { useState, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth-store';
import Maintenance from '../../pages/public/Maintenance';
import MaintenanceBanner from './MaintenanceBanner';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.rumio.world';

export default function MaintenanceGuard({ children }: { children: ReactNode }) {
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  // Fetch maintenance mode status from API
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/maintenance-mode`);
        if (response.ok) {
          const data = await response.json();
          setIsMaintenanceMode(data.enabled);
          setMaintenanceMessage(data.message || '');
        }
      } catch (error) {
        console.error('Failed to check maintenance mode:', error);
      } finally {
        setMaintenanceLoading(false);
      }
    };

    checkMaintenance();

    const interval = setInterval(checkMaintenance, 60000);
    return () => clearInterval(interval);
  }, []);

  // Don't render until maintenance check completes (prevents flash).
  // No need to gate on isLoading — isAdmin is not persisted (defaults to false),
  // so canBypass is always false until initialize() verifies the session.
  if (maintenanceLoading) return null;

  // Compute all flags without early returns so the render tree is always
  // the same shape (2 slots in Fragment). This prevents React from
  // unmounting/remounting children when auth state changes mid-callback.
  const isAuthPage = ['/login', '/signup', '/auth/callback', '/forgot-password', '/reset-password'].some(
    (p) => location.pathname === p || location.pathname.startsWith(p + '/')
  );

  // Don't use server bypass here — the server derives it from an HttpOnly cookie
  // that persists after logout, which would let logged-out users bypass maintenance.
  const canBypass = isAuthenticated && isAdmin;
  const showBanner = isMaintenanceMode && canBypass && !isAuthPage;
  const showMaintenance = isMaintenanceMode && !canBypass && !isAuthPage;

  return (
    <>
      {showBanner && <MaintenanceBanner />}
      {showMaintenance ? <Maintenance message={maintenanceMessage} /> : children}
    </>
  );
}
